import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Permission, Profile, UserScopes } from "src/models";
import { LicenseStatus } from "src/models/hierarchy";
import { RootState } from "src/store/rootStore";
import { authApiSlice } from "./authApiSlice";

export const AUTH_STORAGE_KEY = "pft.auth";

interface AuthSliceState {
  accessToken: string | null;
  refreshToken: string | null;
  /** undefined = loading, null = signed out */
  user: Profile | null | undefined;
  permissions: Permission[] | null;
  scopes: UserScopes | null;
  mfaRequired: boolean;
  /** The caller's district license from `GET /me/permissions` (R19); null for platform admins. */
  license?: { status: LicenseStatus } | null;
}

const initialState: AuthSliceState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  permissions: null,
  scopes: null,
  mfaRequired: false,
  license: null,
};

type StoredAuth = Partial<
  Pick<
    AuthSliceState,
    | "accessToken"
    | "refreshToken"
    | "user"
    | "permissions"
    | "scopes"
    | "license"
  >
>;

export const readStoredAuth = (): StoredAuth => {
  try {
    return JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const persist = (state: AuthSliceState) => {
  try {
    const { accessToken, refreshToken, user, permissions, scopes, license } =
      state;
    window.localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        accessToken,
        refreshToken,
        user,
        permissions,
        scopes,
        license,
      }),
    );
  } catch {
    /* storage unavailable (private mode) */
  }
};

const authSlice = createSlice({
  name: "auth",
  initialState: { ...initialState, ...readStoredAuth() } as AuthSliceState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      persist(state);
    },
    setUser: (
      state,
      action: PayloadAction<{ user: Profile | null | undefined }>,
    ) => {
      state.user = action.payload.user;
      persist(state);
    },
    setPermissions: (
      state,
      action: PayloadAction<{
        permissions: Permission[];
        scopes?: UserScopes;
        mfaRequired?: boolean;
        license?: { status: LicenseStatus } | null;
      }>,
    ) => {
      state.permissions = action.payload.permissions.map(
        ({ action: a, subject }) => ({ action: a, subject }),
      );
      state.scopes = action.payload.scopes ?? null;
      state.mfaRequired = action.payload.mfaRequired ?? false;
      state.license = action.payload.license?.status
        ? { status: action.payload.license.status }
        : null;
      persist(state);
    },
    logOut: (state) => {
      Object.assign(state, initialState);
      try {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      authApiSlice.endpoints.getUser.matchFulfilled,
      (state, action) => {
        if (action.payload?.profile) {
          state.user = action.payload.profile;
          persist(state);
        }
      },
    );
  },
});

export const { setCredentials, setUser, logOut, setPermissions } =
  authSlice.actions;
export const resetAuthSlice = logOut;
export default authSlice.reducer;

export const selectCurrentAccessToken = (state: RootState) =>
  state.auth.accessToken;
export const selectCurrentRefreshToken = (state: RootState) =>
  state.auth.refreshToken;
export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectCurrentPermissions = (state: RootState) =>
  state.auth.permissions;
export const selectCurrentScopes = (state: RootState) => state.auth.scopes;
export const selectMfaRequired = (state: RootState) => state.auth.mfaRequired;
/** The signed-in user's district license status, or null (platform admin, or not yet known). */
export const selectLicenseStatus = (state: RootState): LicenseStatus | null =>
  state.auth.license?.status ?? null;
